import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { getToken } from '@/utils/storage';
import { getStoredGuestExamUser, createTaskAttempt } from '@/api/exam';
import { supabase } from '@/api/supabaseClient';
import NextFocus from "@/components/NextFocus";
import { SkillsRadarChart } from "@/components/SkillsRadarChart";
import TaskAnalysis from "@/components/TaskAnalysis";
import Select, { SingleValue } from "react-select";
import { isTask2MaintenanceEnabled } from "@/config/maintenance";
import Task2MaintenanceNotice from "@/components/task2/Task2MaintenanceNotice";
import rightarrow from '../../public/images/SVG.svg';
import '../styles/feedback.css';
import { useIsMobile } from '@/hooks/use-mobile';
import { availableMemory } from 'process';
import { Lock } from 'lucide-react';
import { pushDL } from "@/lib/analytics";
import apiClient from '@/api/apiClient';
import { setExamMode } from "@/utils/examMode";


interface FeedbackData {
  session_id: string;
  version: string;
  user: {
    id: string;
    avatar_url: string;
  };
  exam_date: string;
  all_data: boolean;
  task_data?: string;
  overall: {
    cefr_level: string;
    goal_level: string;
    goal_position: string;
    motivation_text: string;
  };
  radar: {
    scores: {
      fluency: number;
      grammar: number;
      vocabulary: number;
      pronunciation: number;
      comprehension: number;
    };
    meta: {
      strength: string[];
      focus: string[];
    };
  };
  tasks: Array<{
    index: number;
    type: string;
    title: string;
    cefr_level: string;
    topic_id: string;
    feedback: string;
    reflection: string;
    native_example: {
      available: boolean;
      text_fr: string;
    };


  }>;
  next_focus_tips: Array<{
    tag: string;
    text?: string;
    text_en?: string;
  }>;
}

interface TaskOption {
  value: string;
  label: string;
}
const taskOptions: TaskOption[] = [
  { value: "task1", label: "Task 1" },
  { value: "task2", label: "Task 2" },
  { value: "task3", label: "Task 3" },
];

const getAvailableTaskOptions = () =>
  isTask2MaintenanceEnabled()
    ? taskOptions.filter((option) => option.value !== "task2")
    : taskOptions;

const ComprehensiveFeedbackResults = () => {
  const [feedbackData, setFeedbackData] = useState<FeedbackData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isRetrying, setIsRetrying] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingTask, setPendingTask] = useState<TaskOption | null>(null);
  const [isRetryingSameTopic, setIsRetryingSameTopic] = useState(false);
  const [userPlan, setUserPlan] = useState<string>("Free");
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>("");
  const isAuthenticated = !!getToken();


    // ⭐ Review modal state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isReviewLoading, setIsReviewLoading] = useState(false);
  const [reviewPromptShownInSession, setReviewPromptShownInSession] = useState(false);

  const params = new URLSearchParams(location.search);
  const examIdFromUrl = params.get('examId') || '';
  const isGuestSessionFromUrl = examIdFromUrl.startsWith('guest_');
  // ✅ True when user is revisiting results (should NOT count as a new completed exam)
  const navState: any = (location && (location as any).state) || {};
  const fromParam = params.get("from");
  const isHistoryView = fromParam === "list" || !!navState.feedback;


  const handleConfirmRetry = async () => {
    if (!pendingTask) return;

    if (isTask2MaintenanceEnabled() && pendingTask.value === "task2") {
      setShowConfirmationModal(false);
      setPendingTask(null);
      return;
    }

    switch (pendingTask.value) {
      case "task1":
        await handleRetryTask1();
        break;
      case "task2":
        await handleRetryTask2();
        break;
      case "task3":
        await handleRetryTask3();
        break;
      default:
        console.error("Invalid task selected for retry");
    }

    // Close modal + clear selection
    setShowConfirmationModal(false);
    setPendingTask(null);
  };

  const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  useEffect(() => {
    const fetchUserPlan = async () => {
      if (!isAuthenticated) return;

      try {
        const response = await apiClient.post("/get_user_subscriptions", {});
        const subscriptions = response.data?.subscriptions || [];

        // Get the active or cancel_scheduled subscription
        const activeSub = subscriptions.find(
          (sub: any) => sub.status === "active" || sub.status === "cancel_scheduled"
        );

        const expiredSub = subscriptions.find(
          (sub: any) => sub.status === "expired"
        );

        if (activeSub?.plan_name) {
          setUserPlan(activeSub.plan_name);
          setSubscriptionStatus(activeSub.status);
        } else if (expiredSub) {
          setUserPlan(expiredSub.plan_name);
          setSubscriptionStatus("expired");
        } else {
          setUserPlan("Free");
          setSubscriptionStatus("");
        }
      } catch (error) {
        console.error("Error fetching subscription plan:", error);
        setUserPlan("Free");
        setSubscriptionStatus("");
      }
    };

    fetchUserPlan();
  }, [isAuthenticated]);


  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Close modals on ESC
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (showConfirmationModal) {
          setShowConfirmationModal(false);
          setPendingTask(null);
        }
        if (showReviewModal) {
          handleDismissReview(); // ✅ record snooze state, not just close UI
        }
      }
    };
  
    if (showConfirmationModal || showReviewModal) {
      window.addEventListener("keydown", onKeyDown);
    }
  
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showConfirmationModal, showReviewModal]);

  
  const handleCancelRetry = () => {
    setShowConfirmationModal(false);
    setPendingTask(null);
  };

  useEffect(() => {
    // If navigation state contains feedback (from ExamList), prefer it
    const navState: any = (location && (location as any).state) || {};
    if (navState && navState.feedback) {
      setFeedbackData(navState.feedback as FeedbackData);
      setLoading(false);
      return;
    }

    // If opened from list via query param, attempt to load data from localStorage as a fallback
    const params = new URLSearchParams(location.search);
    const from = params.get('from');
    const examId = params.get('examId');
    if (from === 'list') {
      const stored = localStorage.getItem('combined_feedback_data');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setFeedbackData(parsed);
        } catch (err) {
          console.error('❌ Error parsing stored combined feedback data:', err);
        }
      } else {
        console.warn('⚠️ No combined_feedback_data found in localStorage for examId', examId);
      }
      setLoading(false);
      return; // skip the rest of the effect which expects local data
    }
    // Check if user is a guest
    const paidToken = getToken();
    const guestUser = getStoredGuestExamUser();
    const guestToken = guestUser?.token ?? localStorage.getItem("guestToken");
    const isGuestUser = !paidToken && !!guestToken;

    setIsGuest(isGuestUser);

    // Get feedback data from localStorage
    const storedData = localStorage.getItem('combined_feedback_data');
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        setFeedbackData(parsedData);
        // Store examId for retry functions
        localStorage.setItem('combined_feedback_exam_id', parsedData.session_id);
        setLoading(false);
      } catch (error) {
        console.error('Error parsing feedback data:', error);
        setLoading(false);
      }
    } else {
      console.error('No feedback data found');
      setLoading(false);
    }
  }, []);
  const handleRetryTask2 = async (useSameTopic: boolean = true) => {
    try {
      if (useSameTopic) {
        setIsRetryingSameTopic(true);
      } else {
        setIsRetryingSameTopic(true);
      }
      console.log('🎯 RETRY TASK 2 - Starting retry process...', { useSameTopic });

      // Unlock audio context for iPhone compatibility (same as MicTest and TaskOne)
      try {
        const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (AC) {
          let ctx: AudioContext | undefined = (window as any).__appAudioContext;
          if (!ctx || ctx.state === 'closed') {
            console.log('🔧 Creating new AudioContext in SingleTaskFeedback...');
            ctx = new AC();
            (window as any).__appAudioContext = ctx;
          }

          console.log('🔧 AudioContext state before resume:', ctx.state);

          if (ctx.state !== 'running') {
            console.log('🔧 Resuming AudioContext...');
            await ctx.resume();
            console.log('🔧 AudioContext state after resume:', ctx.state);
          }

          // Additional mobile-specific unlock
          if (ctx.state === 'suspended') {
            console.log('🔧 AudioContext still suspended, trying again...');
            await new Promise(resolve => setTimeout(resolve, 100));
            await ctx.resume();
          }

          // Keep audio context alive on mobile
          const keepAlive = () => {
            if (ctx && ctx.state === 'suspended') {
              ctx.resume().catch(() => { });
            }
          };

          // Resume audio context on user interaction (mobile requirement)
          document.addEventListener('touchstart', keepAlive, { once: true });
          document.addEventListener('touchend', keepAlive, { once: true });
          document.addEventListener('click', keepAlive, { once: true });
        }
        console.log('🔓 Audio context unlocked for Task 2 retry (mobile optimized)');
      } catch (e) {
        console.warn('⚠️ AudioContext unlock failed:', e);
      }

      // Add a small delay to ensure audio context is properly set up
      await new Promise(resolve => setTimeout(resolve, 200));

      const token = getToken();
      const examId = localStorage.getItem('combined_feedback_exam_id');

      if (!examId) {
        console.error('❌ No session ID found for retry');
        alert('No session found. Please start a new exam.');
        return;
      }

      if (!token) {
        console.error('❌ No auth token found for retry');
        alert('Authentication required. Please log in again.');
        return;
      }

      // Delete the previous transcript
      try {
        console.log(`🗑️ Deleting transcript for examId: ${examId}, task: task2`);
        const { error: deleteError } = await supabase.functions.invoke('delete_transcript', {
          body: { examId, task: 'task2' },
        });
        if (deleteError) {
          console.error('❌ Error deleting transcript:', deleteError);
        } else {
          console.log('✅ Transcript deleted successfully');
        }
      } catch (error) {
        console.error('❌ Exception deleting transcript:', error);
      }

      console.log('🎯 RETRY TASK 2 - Creating task attempt for session:', examId);

      // Create a new task attempt entry
      const attemptResponse = await createTaskAttempt({
        sessionId: examId,
        taskType: 2 // Task 2
      });

      if (attemptResponse.success) {
        console.log('✅ RETRY TASK 2 - Task attempt created successfully:', attemptResponse);

        // Store the attempt ID for later use when task is completed
        if (attemptResponse.taskAttemptId) {
          localStorage.setItem('current_task_attempt_id', attemptResponse.taskAttemptId);
          console.log('💾 RETRY TASK 2 - Stored attempt ID:', attemptResponse.taskAttemptId);
        }

        // Store topic preference for Task 2
        localStorage.setItem('task2_retry_use_same_topic', useSameTopic.toString());
        console.log('💾 RETRY TASK 2 - Stored topic preference:', useSameTopic);

        // Store task type for retry context
        localStorage.setItem('current_retry_task_type', '2');
        console.log('💾 RETRY TASK 2 - Stored task type for retry context: 2');

        // Show success message with attempt number
        const attemptNumber = attemptResponse.attemptNumber || 1;
        console.log(`🎯 RETRY TASK 2 - Starting attempt #${attemptNumber}...`);

        // Navigate to Task 2 with the existing session ID
        console.log('🎯 RETRY TASK 2 - Navigating to Task 2...');
        navigate('/task-two', { state: { examId: examId } });
      } else {
        console.error('❌ RETRY TASK 2 - Failed to create task attempt:', attemptResponse.message);
        alert('Failed to start retry. Please try again.');
      }
    } catch (error) {
      console.error('❌ RETRY TASK 2 - Error during retry:', error);
      alert('An error occurred while starting the retry. Please try again.');
    } finally {
      if (useSameTopic) {
        setIsRetryingSameTopic(false);
      } else {
        setIsRetryingSameTopic(false);
      }
    }
  };

  const handleRetryTask3 = async () => {
    try {
      setIsRetrying(true);
      console.log('🎯 RETRY TASK 3 - Starting retry process...');

      // Unlock audio context for iPhone compatibility (same as MicTest and TaskOne)
      try {
        const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (AC) {
          let ctx: AudioContext | undefined = (window as any).__appAudioContext;
          if (!ctx || ctx.state === 'closed') {
            console.log('🔧 Creating new AudioContext in SingleTaskFeedback...');
            ctx = new AC();
            (window as any).__appAudioContext = ctx;
          }

          console.log('🔧 AudioContext state before resume:', ctx.state);

          if (ctx.state !== 'running') {
            console.log('🔧 Resuming AudioContext...');
            await ctx.resume();
            console.log('🔧 AudioContext state after resume:', ctx.state);
          }

          // Additional mobile-specific unlock
          if (ctx.state === 'suspended') {
            console.log('🔧 AudioContext still suspended, trying again...');
            await new Promise(resolve => setTimeout(resolve, 100));
            await ctx.resume();
          }

          // Keep audio context alive on mobile
          const keepAlive = () => {
            if (ctx && ctx.state === 'suspended') {
              ctx.resume().catch(() => { });
            }
          };

          // Resume audio context on user interaction (mobile requirement)
          document.addEventListener('touchstart', keepAlive, { once: true });
          document.addEventListener('touchend', keepAlive, { once: true });
          document.addEventListener('click', keepAlive, { once: true });
        }
        console.log('🔓 Audio context unlocked for Task 3 retry (mobile optimized)');
      } catch (e) {
        console.warn('⚠️ AudioContext unlock failed:', e);
      }

      // Add a small delay to ensure audio context is properly set up
      await new Promise(resolve => setTimeout(resolve, 200));

      const token = getToken();
      const examId = localStorage.getItem('combined_feedback_exam_id');

      if (!examId) {
        console.error('❌ No session ID found for retry');
        alert('No session found. Please start a new exam.');
        return;
      }

      if (!token) {
        console.error('❌ No auth token found for retry');
        alert('Authentication required. Please log in again.');
        return;
      }

      // Delete the previous transcript
      try {
        console.log(`🗑️ Deleting transcript for examId: ${examId}, task: task3`);
        const { error: deleteError } = await supabase.functions.invoke('delete_transcript', {
          body: { examId, task: 'task3' },
        });
        if (deleteError) {
          console.error('❌ Error deleting transcript:', deleteError);
        } else {
          console.log('✅ Transcript deleted successfully');
        }
      } catch (error) {
        console.error('❌ Exception deleting transcript:', error);
      }

      console.log('🎯 RETRY TASK 3 - Creating task attempt for session:', examId);

      // Create a new task attempt entry
      const attemptResponse = await createTaskAttempt({
        sessionId: examId,
        taskType: 3 // Task 3
      });

      if (attemptResponse.success) {
        console.log('✅ RETRY TASK 3 - Task attempt created successfully:', attemptResponse);

        // Store the attempt ID for later use when task is completed
        if (attemptResponse.taskAttemptId) {
          localStorage.setItem('current_task_attempt_id', attemptResponse.taskAttemptId);
          console.log('💾 RETRY TASK 3 - Stored attempt ID:', attemptResponse.taskAttemptId);
        }

        // Store task type for retry context
        localStorage.setItem('current_retry_task_type', '3');
        console.log('💾 RETRY TASK 3 - Stored task type for retry context: 3');

        // Show success message with attempt number
        const attemptNumber = attemptResponse.attemptNumber || 1;
        console.log(`🎯 RETRY TASK 3 - Starting attempt #${attemptNumber}...`);

        // Individual Task retry is Full/paid authority.
        setExamMode("full");
        // Navigate to Task 3 with the existing session ID
        console.log('🎯 RETRY TASK 3 - Navigating to Task 3...');
        navigate('/task-three', { state: { examId: examId } });
      } else {
        console.error('❌ RETRY TASK 3 - Failed to create task attempt:', attemptResponse.message);
        alert('Failed to start retry. Please try again.');
      }
    } catch (error) {
      console.error('❌ RETRY TASK 3 - Error during retry:', error);
      alert('An error occurred while starting the retry. Please try again.');
    } finally {
      setIsRetrying(false);
    }
  };

  const getCefrColor = (level: string) => {
    const colors = {
      'A1': 'bg-red-100 text-red-800',
      'A2': 'bg-orange-100 text-orange-800',
      'B1': 'bg-yellow-100 text-yellow-800',
      'B2': 'bg-blue-100 text-blue-800',
      'C1': 'bg-green-100 text-green-800',
      'C2': 'bg-purple-100 text-purple-800'
    };
    return colors[level as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getGoalPositionColor = (position: string) => {
    switch (position) {
      case 'above_goal':
        return 'bg-green-100 text-green-800';
      case 'at_goal':
        return 'bg-blue-100 text-blue-800';
      case 'below_goal':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getGoalPositionText = (position: string) => {
    switch (position) {
      case 'above_goal':
        return 'Exceeded Goal';
      case 'at_goal':
        return 'Achieved Goal';
      case 'below_goal':
        return 'Working Towards Goal';
      default:
        return 'Unknown';
    }
  };
  const handleTaskSelect = (selectedOption: SingleValue<TaskOption>) => {
    if (selectedOption) {
      setPendingTask(selectedOption);
      setShowConfirmationModal(true);
    }
  };

  const handleConfirmReview = () => {
    try {
      setIsReviewLoading(true);

      // Mark as reviewed so we never ask again later
      localStorage.setItem("p2p_has_left_review", "true");

      navigate("/leave-review");
    } catch (err) {
      console.error("Error navigating to review page:", err);
    } finally {
      setIsReviewLoading(false);
      setShowReviewModal(false);
    }
  };

  // ⭐ When user clicks "Not now" (or ESC/backdrop)
  function handleDismissReview() {
    try {
      localStorage.setItem("p2p_review_last_dismissed_at", new Date().toISOString());
  
      // Record exam count at dismissal so we can require "2 more full exams"
      const raw = localStorage.getItem("p2p_exam_count");
      const current = raw ? parseInt(raw, 10) || 0 : 0;
      localStorage.setItem("p2p_review_dismissed_exam_count", String(current));
    } catch (err) {
      console.error("Error saving review dismissal:", err);
    }
    setShowReviewModal(false);
  }

  const handleRetryTask1 = async () => {
    try {
      setIsRetrying(true);
      console.log('🎯 RETRY TASK 1 - Starting retry process...');

      // Unlock audio context
      try {
        const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (AC) {
          let ctx: AudioContext | undefined = (window as any).__appAudioContext;
          if (!ctx || ctx.state === 'closed') {
            ctx = new AC();
            (window as any).__appAudioContext = ctx;
          }
          if (ctx.state !== 'running') {
            await ctx.resume();
          }
        }
      } catch (e) {
        console.warn('⚠️ AudioContext unlock failed:', e);
      }

      await new Promise(resolve => setTimeout(resolve, 200));

      const token = getToken();
      const examId = localStorage.getItem('combined_feedback_exam_id');

      if (!examId) {
        console.error('❌ No session ID found for retry');
        alert('No session found. Please start a new exam.');
        setIsRetrying(false);
        return;
      }

      if (!token) {
        console.error('❌ No auth token found for retry');
        alert('Authentication required. Please log in again.');
        setIsRetrying(false);
        return;
      }

      // Delete previous transcript
      try {
        console.log(`🗑️ Deleting transcript for examId: ${examId}, task: task1`);
        const { error: deleteError } = await supabase.functions.invoke('delete_transcript', {
          body: { examId, task: 'task1' },
        });
        if (deleteError) {
          console.error('❌ Error deleting transcript:', deleteError);
        } else {
          console.log('✅ Transcript deleted successfully');
        }
      } catch (error) {
        console.error('❌ Exception deleting transcript:', error);
      }

      // Create new task attempt
      const attemptResponse = await createTaskAttempt({
        sessionId: examId,
        taskType: 1,
      });

      if (attemptResponse.success) {
        console.log('✅ RETRY TASK 1 - Task attempt created successfully:', attemptResponse);

        if (attemptResponse.taskAttemptId) {
          localStorage.setItem('current_task_attempt_id', attemptResponse.taskAttemptId);
        }
        localStorage.setItem('current_retry_task_type', '1');

        navigate('/task-one', { state: { examId: examId } });
      } else {
        console.error('❌ RETRY TASK 1 - Failed to create task attempt:', attemptResponse.message);
        alert('Failed to start retry. Please try again.');
      }
    } catch (error) {
      console.error('❌ RETRY TASK 1 - Error during retry:', error);
      alert('An error occurred while starting the retry. Please try again.');
    } finally {
      setIsRetrying(false);
      setShowConfirmationModal(false);
    }
  };




  const handleUpgrade = () => {
  try {
    pushDL("upgrade_click", {
      exam_type: isGuest ? "trial" : "paid",
      source: "unlock_my_full_feedback_button",
      page_path: location.pathname,
      page_location: window.location.href,
    });
  } catch (err) {
    console.error("❌ Failed to push upgrade_click:", err);
  }

  // optional attribution (does NOT affect functionality)
  sessionStorage.setItem("p2p_checkout_source", "unlock_feedback");

  navigate("/plans");
};




  // Generate dummy data for guest users
  const getDummyFeedbackData = (): FeedbackData => {
    // Pull real CEFR level, motivation, and all_data from actual feedback or localStorage fallback
    let cefrLevel = 'B2';
    let motivationText = 'Keep practicing to reach your goal level. You\'re making progress.';
    let goalLevel = 'B2';
    let goalPosition = 'at_goal';
    let allData = true;
    let taskData = '';

    try {
      const stored = JSON.parse(localStorage.getItem('combined_feedback_data') || '{}');
      cefrLevel = (feedbackData?.overall?.cefr_level) || (stored?.overall?.cefr_level) || 'B2';
      motivationText = (feedbackData?.overall?.motivation_text) || (stored?.overall?.motivation_text) || 'Keep practicing to reach your goal level. You\'re making progress.';
      goalLevel = (feedbackData?.overall?.goal_level) || (stored?.overall?.goal_level) || 'B2';
      goalPosition = (feedbackData?.overall?.goal_position) || (stored?.overall?.goal_position) || 'at_goal';
      allData = (feedbackData?.all_data !== undefined) ? feedbackData.all_data : ((stored?.all_data !== undefined) ? stored.all_data : true);
      taskData = feedbackData?.task_data || stored?.task_data || '';
    } catch { }

    return {
      session_id: feedbackData?.session_id || 'dummy_session',
      version: 'feedback.v1',
      user: {
        id: 'dummy_user',
        avatar_url: ''
      },
      exam_date: new Date().toISOString(),
      all_data: allData,
      task_data: taskData,
      overall: {
        cefr_level: cefrLevel,
        goal_level: goalLevel,
        goal_position: goalPosition,
        motivation_text: motivationText
      },
      radar: {
        scores: {
          fluency: 75,
          grammar: 65,
          vocabulary: 70,
          pronunciation: 60,
          comprehension: 80
        },
        meta: {
          strength: ['comprehension', 'fluency'],
          focus: ['pronunciation', 'grammar']
        }
      },
      tasks: [
        {
          index: 1,
          type: 'self_intro',
          title: 'Task 1 — Self introduction',
          cefr_level: 'B1',
          topic_id: 't_101',
          feedback: `Pour cette première tâche, vous parvenez à vous présenter de manière claire et structurée. 
          Vous donnez les informations essentielles sur votre situation personnelle et professionnelle, et votre discours est globalement fluide. 
          Le vocabulaire utilisé est approprié au contexte et vous montrez une bonne maîtrise des temps de base, notamment le présent et le passé récent.
          Au niveau B2, on attend une certaine aisance dans l’expression orale, ce qui est visible dans votre capacité à développer vos idées sans trop d’hésitations. 
          Toutefois, certaines phrases restent assez simples sur le plan syntaxique, et l’on observe peu de reformulations ou d’expressions nuancées. 
          Pour renforcer votre performance, vous pourriez enrichir davantage vos réponses avec des exemples concrets ou des détails personnels supplémentaires, 
          afin de rendre votre présentation plus naturelle et plus vivante.`,
          
          reflection: `Cette tâche permet d’évaluer votre capacité à parler de vous de façon organisée et spontanée. 
          À ce niveau, il ne s’agit pas seulement de transmettre des informations, mais aussi de montrer que vous êtes à l’aise à l’oral. 
          Une bonne stratégie consiste à structurer votre présentation autour de trois axes clairs : votre situation actuelle, votre parcours, et vos projets.
          Pour progresser au-delà du B2, vous pouvez travailler sur l’allongement des phrases, l’utilisation de connecteurs variés et l’intégration d’exemples personnels plus précis. 
          Cela permet de démontrer une meilleure maîtrise du discours continu, tout en conservant un ton naturel et authentique.`,
          native_example: {

            available: true,
            text_fr: `Bonjour, je m’appelle Matteo. Je viens d’Italie mais je vis à Québec depuis trois ans. J’habite dans Saint Jean Baptiste, un quartier que j’adore pour son charme, ses commerces locaux et son rythme de vie tranquille. Je m’y sens comme dans un petit village où tout le monde finit par se connaître.

Je suis chef dans un restaurant italien contemporain. Mon travail consiste à créer des menus, à superviser la cuisine et à imaginer des recettes qui mélangent des saveurs traditionnelles et québécoises. J’adore expérimenter : remplacer un ingrédient classique par un produit local, revisiter une recette de ma grand mère ou encore inventer une assiette à partir d’une inspiration du moment. Ce métier demande beaucoup d’énergie, mais j’aime l’intensité, la créativité et l’esprit d’équipe qui règnent dans une cuisine.

Dans ma vie personnelle, je suis quelqu’un qui aime partager. J’organise souvent des soirées où je cuisine pour mes amis. Je fais aussi beaucoup de sport, surtout de la natation, pour me vider la tête. Le week-end, j’aime aller au marché, parler avec les producteurs, découvrir de nouveaux ingrédients. C’est une vraie source d’inspiration pour moi.

Je prépare le TCF parce que j’aimerais enseigner la cuisine un jour, ou même ouvrir un petit atelier culinaire destiné à la communauté francophone. Pour ça, je dois être vraiment à l’aise en français. Je m’entraîne donc régulièrement pour enrichir mon vocabulaire et m’exprimer avec plus de fluidité.`
          },

        },
        {
          index: 2,
          type: 'roleplay_qa',
          title: 'Task 2 — Roleplay questions',
          cefr_level: 'B2',
          topic_id: 't_202',
          feedback: `Dans cette tâche, vous démontrez une bonne capacité à interagir dans une situation formelle ou semi-formelle. 
          Votre introduction est appropriée et vous adoptez un ton poli tout au long de l’échange. 
          Les questions posées sont pertinentes, bien formulées et couvrent l’essentiel des informations attendues par rapport au sujet.
          Le fait d’avoir posé environ huit questions montre une bonne gestion du temps et une compréhension claire de l’objectif de la tâche. 
          Vous variez les types de questions et vous adaptez votre langage au contexte, ce qui correspond bien aux attentes du niveau B2. 
          La conclusion, avec une formule de remerciement, est également cohérente et naturelle.
          Cependant, certaines questions pourraient être davantage développées ou reformulées afin de créer un enchaînement plus fluide entre les idées. 
          Pour améliorer votre performance, vous pourriez intégrer davantage de relances ou de transitions, ce qui renforcerait l’aspect interactif de l’échange.`,
          reflection: `La tâche 2 vise à évaluer votre capacité à poser des questions de manière efficace et naturelle dans un contexte donné. 
          À partir du niveau B2, l’examinateur attend non seulement des questions correctes sur le plan grammatical, mais aussi une certaine souplesse dans l’interaction.
          Pour progresser, il est utile de penser à l’échange comme à une vraie conversation. 
          Cela implique de rebondir sur les réponses de l’interlocuteur, de reformuler certaines informations et d’utiliser des expressions de transition pour lier les questions entre elles. 
          Une bonne conclusion, comme celle que vous avez proposée, contribue également à donner une impression professionnelle et maîtrisée.`,
          native_example: {

            available: true,
            text_fr: `Bonjour, j’ai entendu parler de votre groupe et cela m’a vraiment donné envie d’en savoir plus. J’aimerais vous poser quelques questions pour comprendre comment tout cela fonctionne.

1. Quel type d’aide vous apportez concrètement aux personnes âgées ?  
2. Vous intervenez chez elles ou dans un lieu précis ?  
3. À quelle fréquence vous organisez vos visites ou vos activités ?  
4. Une séance d’aide ou d’accompagnement dure en général combien de temps ?  
5. Est-ce qu’il faut s’inscrire à l’avance pour rejoindre le groupe ?  
6. Quels types d’activités vous proposez le plus souvent ?  
7. Y a-t-il des bénévoles expérimentés qui accompagnent les nouveaux au début ?  
8. Vous acceptez des personnes sans expérience dans le bénévolat ?  
9. Est-ce qu’il y a des règles particulières à respecter lorsqu’on aide quelqu’un ?  
10. Les interventions se font toujours en binôme ou parfois seul ?  
11. Y a-t-il des frais d’adhésion ou une contribution financière pour participer ?  
12. Peut-on choisir les jours où l’on souhaite s’engager ?

Merci beaucoup pour toutes ces précisions. Je vais y réfléchir sérieusement et voir comment je pourrais m’engager avec vous.`
          },

        },
        {
          index: 3,
          type: 'opinion_argument',
          title: 'Task 3 — Opinion and argument',
          cefr_level: 'B2',
          topic_id: 't_303',
          feedback: `Vous exprimez une opinion claire et compréhensible sur le rôle des réseaux sociaux dans les relations humaines. 
          Votre réponse est bien structurée, avec une introduction du sujet, des arguments développés et une conclusion cohérente. 
          Vous utilisez un vocabulaire adapté au thème et des exemples pertinents pour illustrer vos idées.
          Au niveau B2, on attend une capacité à argumenter de manière logique, ce que vous parvenez à faire. 
          Vous montrez également une certaine nuance en reconnaissant que les réseaux sociaux peuvent avoir des effets à la fois positifs et négatifs. 
          Toutefois, certaines idées pourraient être approfondies davantage afin de renforcer la force de l’argumentation, 
          notamment en expliquant plus précisément les conséquences ou en comparant différentes situations.
          Pour viser un niveau supérieur, vous pourriez travailler sur l’utilisation de connecteurs plus variés et sur 
          la formulation d’arguments plus complexes, en évitant les structures trop répétitives.`,
          reflection: `Cette tâche évalue votre capacité à défendre une opinion personnelle de manière structurée et convaincante. 
          À ce niveau, il est important de ne pas se contenter d’énoncer des idées générales, mais de les développer avec des exemples concrets et une logique claire.
          Pour progresser, vous pouvez vous entraîner à enrichir vos arguments en ajoutant des comparaisons, des causes et des conséquences, ou encore des contre-arguments suivis d’une réfutation. 
          Cela permet de montrer une meilleure maîtrise du discours argumentatif et d’atteindre un niveau de précision et de nuance plus avancé.`,
          native_example: {
            available: true,
            text_fr: `Les réseaux sociaux occupent aujourd’hui une place centrale dans la manière dont nous communiquons. Certains affirment qu’ils rapprochent les individus en facilitant les échanges, tandis que d’autres pensent qu’ils ont tendance à créer une distance émotionnelle. Selon moi, les réseaux sociaux peuvent rapprocher ou éloigner les gens, mais tout dépend de la manière dont ils sont utilisés.

Tout d’abord, les réseaux sociaux offrent une facilité de communication sans précédent. Ils permettent de rester en contact avec des amis et des membres de la famille, même lorsqu’ils vivent dans un autre pays. Ils offrent également la possibilité de créer de nouveaux liens, de rejoindre des groupes d’intérêt commun et d’échanger avec des personnes que l’on n’aurait jamais rencontrées autrement. Cette ouverture sur le monde représente un avantage majeur pour ceux qui cherchent à élargir leurs horizons.

Ensuite, les réseaux sociaux permettent de partager des moments importants de sa vie, comme des voyages, des réussites personnelles ou des événements familiaux. Ces partages créent une impression de proximité et donnent aux autres la possibilité de suivre notre quotidien. Dans certains cas, cela peut même renforcer des relations qui seraient autrement plus distantes.

Cependant, il est vrai que les réseaux sociaux peuvent aussi créer une forme de distance émotionnelle. Les interactions en ligne ne remplacent pas toujours la profondeur des relations en face à face. Les échanges sont souvent rapides, superficiels ou basés sur des réactions instantanées, comme des mentions "j’aime". De plus, les réseaux sociaux peuvent parfois susciter des comparaisons ou des complexes, car les utilisateurs y montrent souvent une version idéalisée de leur vie.

Un autre problème est la dépendance que ces plateformes peuvent provoquer. Beaucoup de personnes passent des heures à faire défiler des contenus, ce qui réduit le temps consacré aux interactions réelles. Cette utilisation excessive peut entraîner un isolement progressif, même lorsque l’on est entouré de nombreuses personnes virtuelles.

Il est donc essentiel d’adopter une utilisation équilibrée et consciente des réseaux sociaux. Les plateformes en elles-mêmes ne sont ni bonnes ni mauvaises. Ce sont les comportements qu’elles encouragent qui influencent la qualité des relations humaines.

En conclusion, les réseaux sociaux peuvent rapprocher les individus en facilitant la communication, mais ils peuvent aussi créer des distances émotionnelles s’ils deviennent un substitut aux relations réelles. Le plus important est d’utiliser ces outils de manière réfléchie et de ne jamais oublier que rien ne remplace la richesse d’une interaction humaine authentique.`
          },
        }
      ],
      next_focus_tips: [
        { tag: 'grammar', text_en: 'Use longer sentences with subordinate clauses (e.g. qui, même si, lorsque) instead of short, simple phrases.' },
        { tag: 'vocabulary', text_en: 'Replace general words with more precise vocabulary, especially for abstract topics and opinions.' },
        { tag: 'fluency', text_en: 'Link your ideas more smoothly using connectors like d’abord, en revanche, par conséquent.' }
      ]
    };
  };

  // Get the data to display (real data for paid users, dummy data for guests)
  const displayData = isGuest ? getDummyFeedbackData() : feedbackData;
  useEffect(() => {
    console.log("displayData:", displayData);
    }, [displayData])

    // ✅ GA4/GTM — exam_complete when results page is actually viewable
    useEffect(() => {
      if (!displayData) return;
    
      try {
        const eventExamId = examIdFromUrl || displayData.session_id || "unknown_exam";
        const dedupeKey = "combined_feedback_exam_id";
    
        const lastFired = localStorage.getItem(dedupeKey);
        if (lastFired === eventExamId) return;
    
        pushDL("exam_complete", {
          exam_type: isGuest ? "trial" : "paid",
          exam_id: eventExamId,
          cefr_level: displayData?.overall?.cefr_level || "",
          goal_level: displayData?.overall?.goal_level || "",
          all_data: String(displayData?.all_data ?? ""),
          page_path: location.pathname,
          page_location: window.location.href,
        });
      } catch (err) {
        console.error("❌ Failed to push exam_complete to dataLayer:", err);
      }
    }, [displayData, isGuest, examIdFromUrl, location.pathname]);

  
   // ⭐ Track how many full exams this user has completed
  useEffect(() => {
    if (!displayData || isGuest) return;
  
    // ✅ Only count true full exams
    if (displayData.all_data !== true) return;
  
    // ✅ Do NOT count when user is revisiting results
    if (isHistoryView) return;
  
    try {
      const lastCountedId = localStorage.getItem("p2p_last_counted_exam_id");
      if (lastCountedId === displayData.session_id) {
        // Already counted this exam, don't double-count on refresh
        return;
      }
  
      const raw = localStorage.getItem("p2p_exam_count");
      const current = raw ? parseInt(raw, 10) || 0 : 0;
      const updated = current + 1;
  
      localStorage.setItem("p2p_exam_count", updated.toString());
      localStorage.setItem("p2p_last_counted_exam_id", displayData.session_id);
  
      console.log("🎯 Updated exam count for review prompt:", updated);
    } catch (err) {
      console.error("Failed to update exam count for review prompt:", err);
    }
  }, [displayData, isGuest, isHistoryView]);

  // ⭐ Scroll-based trigger: show review modal near bottom after 3+ full paid exams
  useEffect(() => {
    if (!displayData || isGuest) return;
  
    // ✅ Only after true full exam results
    if (displayData.all_data !== true) return;
  
    // ✅ Never show when user is revisiting results (dashboard list / saved view)
    if (isHistoryView) return;
  
    let examCount = 0;
    try {
      const raw = localStorage.getItem("p2p_exam_count");
      examCount = raw ? parseInt(raw, 10) || 0 : 0;
    } catch {
      examCount = 0;
    }
  
    const hasLeftReview = localStorage.getItem("p2p_has_left_review") === "true";
  
    // ✅ Snooze rule: show again only if (14 days passed) AND (2 more full exams completed)
    const lastDismissRaw = localStorage.getItem("p2p_review_last_dismissed_at");
    const dismissedCountRaw = localStorage.getItem("p2p_review_dismissed_exam_count");
  
    let isSnoozed = false;
    if (lastDismissRaw) {
      const last = new Date(lastDismissRaw).getTime();
      const now = Date.now();
      const daysSince = (now - last) / (1000 * 60 * 60 * 24);
  
      const dismissedCount = dismissedCountRaw
      ? (parseInt(dismissedCountRaw, 10) || 0)
      : examCount; // ✅ backwards-compat: treat missing key as "dismissed at current count"
      const examsSinceDismiss = examCount - dismissedCount;
  
      // Still snoozed until BOTH conditions are satisfied
      if (daysSince < 14 || examsSinceDismiss < 2) {
        isSnoozed = true;
      }
    }
  
    // Conditions where we do NOT show the prompt
    if (hasLeftReview || isSnoozed || examCount < 3 || reviewPromptShownInSession) {
      return;
    }
  
    const handleScroll = () => {
      const scrollPosition = window.innerHeight + window.scrollY;
      const threshold = document.body.offsetHeight - 300; // 300px from bottom
  
      if (scrollPosition >= threshold) {
        setShowReviewModal(true);
        setReviewPromptShownInSession(true);
        window.removeEventListener("scroll", handleScroll);
      }
    };
  
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [displayData, isGuest, reviewPromptShownInSession, isHistoryView]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-6 min-h-screen">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-muted-foreground">Loading your results...</p>
      </div>
    );
  }

  if (!displayData) {
    return (
      <div className="flex flex-col items-center justify-center p-6 min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Results not available</h1>
          <p className="text-muted-foreground mb-6">We couldn't load your feedback results.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1264px] mx-auto px-4 sm:px-6">
      {showConfirmationModal && pendingTask && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onMouseDown={(e) => {
              // Only close when clicking the backdrop itself (not inside modal)
              if (e.target === e.currentTarget) handleCancelRetry();
            }}
          >
          {/* Modal Card */}
          <div className="bg-white rounded-2xl shadow-2xl w-[90%] max-w-md p-6 sm:p-8">
            {subscriptionStatus === "expired" ? (
              <>
                {/* Header */}
                <div className="text-left space-y-3">
                  <h6 className="text-[20px] font-semibold text-gray-900">
                    Your plan has expired. Renew to continue practicing.
                  </h6>
                </div>

                {/* Footer Buttons */}
                <div className="flex justify-end gap-4 mt-8">
                  <button
                    onClick={handleCancelRetry}
                    className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors duration-150"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={() => navigate('/plans')}
                    className="px-6 py-2 rounded-lg font-semibold text-white transition-colors duration-150 bg-[#005FCC] hover:bg-[#0049A8]"
                  >
                    Renew Plan
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Header */}
                <div className="text-left space-y-3">
                  <h6 className="text-[20px] font-semibold text-gray-900">
                    Retry {pendingTask.label}
                  </h6>
                  <p className="text-gray-600 text-base leading-relaxed mt-[7px]">
                    You’ll retry this task using the same topic. Would you like to continue?
                  </p>
                </div>

                {/* Footer Buttons */}
                <div className="flex justify-end gap-4 mt-8">
                  <button
                    onClick={handleCancelRetry}
                    className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors duration-150"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleConfirmRetry}
                    disabled={isRetrying || isRetryingSameTopic}
                    className={`px-6 py-2 rounded-lg font-semibold text-white transition-colors duration-150 ${
                      isRetrying || isRetryingSameTopic
                        ? "cursor-not-allowed opacity-60 bg-[#005FCC]"
                        : "bg-[#005FCC] hover:bg-[#0049A8]"
                    }`}
                  >
                    {isRetrying || isRetryingSameTopic ? "Starting..." : "Retry Now"}
                  </button>
                </div>
              </>
            )}
          </div>

        </div>
      )}

      {isMobile && !isGuest && (
        <div className="task-analysis-footer" >
          <div className='task-analysis-footer-inner' onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
            <img src={rightarrow} alt="" />
            <button className="w-full">
              Back to Dashboard
            </button>
          </div>
        </div>
      )}

            {showReviewModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) handleDismissReview();
            }}
          >
          <div className="bg-white rounded-2xl shadow-2xl w-[90%] max-w-md p-6 sm:p-8">
            {/* Header */}
            <div className="text-left space-y-3">
              <h6 className="text-[20px] font-semibold text-gray-900">
                We’d love to hear your experience
              </h6>
              <p className="text-gray-600 text-base leading-relaxed mt-[7px]">
                Your feedback helps us improve Prep2Pass.
                Would you mind sharing a quick review?
              </p>
            </div>
          
            {/* Footer Buttons */}
            <div className="flex justify-end gap-4 mt-8">
              <button
                onClick={handleDismissReview}
                className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors duration-150"
              >
                Not now
              </button>
          
              <button
                onClick={handleConfirmReview}
                disabled={isReviewLoading}
                className={`px-6 py-2 rounded-lg font-semibold text-white transition-colors duration-150 ${
                  isReviewLoading
                    ? "cursor-not-allowed opacity-60 bg-[#005FCC]"
                    : "bg-[#005FCC] hover:bg-[#0049A8]"
                }`}
              >
                {isReviewLoading ? "Opening..." : "Leave a Review"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="final-results-container">

        <div className="mx-auto main-feedback-content">
          {/* Mobile Back to Dashboard Button */}

          {/* Header */}
          <div className="text-center final-feedback-header">
            <div className='task-analysis-header-2'>
              <h1 className="fianl-feedback-title">
                Your TCF speaking results
              </h1>
              <p className="fianl-feedback-title-date">{new Date(displayData.exam_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
            <div
              className="task-analysis-cefr-level"
              style={{
                background: "linear-gradient(135deg, #005FCC 15%, #00A3FF 87.19%)",
              }}
            >
              <span>
                {displayData.overall.cefr_level}
              </span>
            </div>
            <p className="fianl-feedback-title-subtitle">
              {displayData.overall.motivation_text}
            </p>
          </div>

          {/* Radar Chart */}
          <div className="main-continer-radar">
            <div className="flex justify-center w-full items-center gap-2">
              <span className="fianl-feedback-title-rader">Your skill breakdown</span>
              {isGuest && <Lock className="w-5 h-5 text-gray-500" />}
            </div>
            <div className="radar-chat">
              <div className={`${isGuest ? 'blur-sm' : ''}`}>
                <SkillsRadarChart 
                  data={['fluency', 'grammar', 'vocabulary', 'pronunciation', 'comprehension'].map(skill => ({ 
                    skill, 
                    score: displayData.radar.scores[skill as keyof typeof displayData.radar.scores] || 0 
                  }))} 
                />
              </div>
              <div className="flex flex-col justify-center items-center w-full text-center rader-text">
                {/* <p>{displayData.overall.motivation_text}</p> */}
                {/* <p>To reach the next level, work on building stronger sentences and adding richer vocabulary.</p> */}
                {/* <p><span>Strength:</span> {displayData.radar.meta.strength.join(', ')} · <span>Focus:</span> {displayData.radar.meta.focus.join(', ')}</p> */}
                <p>
                  {displayData?.radar?.meta && (() => {
                    const strength = (displayData.radar.meta.strength || [])
                      .filter((s) => s && s.trim() !== "")
                      .map(capitalizeFirstLetter);

                    const focus = (displayData.radar.meta.focus || [])
                      .filter((f) => f && f.trim() !== "")
                      .map(capitalizeFirstLetter)

                    return (
                      <>
                        {strength.length > 0 && (
                          <>
                            <span>Strength:</span> <span style={{ color: '#616875' }} className={isGuest ? 'blur-sm blur-fade' : ''}>{strength.join(" and ")}</span>
                          </>
                        )}
                        {strength.length > 0 && focus.length > 0 && " · "}
                        {focus.length > 0 && (
                          <>
                            <span>Focus:</span> <span style={{ color: '#616875' }} className={isGuest ? 'blur-sm blur-fade' : ''}>{focus.join(" and ")}</span>
                          </>
                        )}
                      </>
                    );
                  })()}
                </p>

              </div>
            </div>
          </div>

          <div className="task-analysis-section">
            <div className='task-analysis-header-2'>
              <div className="flex items-center gap-2">
                <h2 className="task-analysis-title">
                  Detailed task analysis
                </h2>
                {isGuest && <Lock className="w-5 h-5 text-gray-500" />}
              </div>
              <p className="task-analysis-subtitle">
                Review your responses and see how to improve
              </p>
            </div>
            <TaskAnalysis isGuest={isGuest} displayData={displayData} taskData={displayData?.task_data || ''} />
          </div>


          <div className='next-focus-container'>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 w-full">
              <div className="next-focus-left flex items-center gap-2">
                <span>Your next focus</span>
                {isGuest && <Lock className="w-5 h-5 text-gray-500" />}
              </div>
              {!isGuestSessionFromUrl && !isGuest && (
                <div className="next-focus-right flex-shrink-0">
                  <span className="retake-text">Retake</span>
                  {isTask2MaintenanceEnabled() ? (
                    <Task2MaintenanceNotice compact className="mb-3 mt-2 max-w-sm" />
                  ) : null}
                  <div className="relative">
                    <Select
                      onChange={handleTaskSelect}
                      options={getAvailableTaskOptions()}
                      placeholder="Select a task"
                      isSearchable={false}
                      components={{
                        IndicatorSeparator: () => null,
                      }}
                      styles={{
                        container: (base) => ({
                          ...base,
                          width: "100%",
                          maxWidth: "180px",
                        }),
                        control: (base, state) => ({
                          ...base,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                          minHeight: "40px",
                          padding: "0 12px",
                          borderRadius: "10px",
                          borderWidth: "1px",
                          borderStyle: "solid",
                          borderColor: state.isFocused ? "#2563eb" : "#d1d5db",
                          boxShadow: "none",
                          backgroundColor: "#fff",
                          transition: "all 100ms",
                          fontSize: "14px",
                          cursor: "pointer",
                          "&:hover": { borderColor: "#2563eb" },
                        }),
                        placeholder: (base, state) => ({
                          ...base,
                          color: state.isFocused ? "black" : "#9ca3af",
                          fontSize: "14px",
                          fontWeight: "400",
                          transition: "color 0.2s ease",
                        }),

                        valueContainer: (base) => ({
                          ...base,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "0",
                          margin: "0",
                          width: "100%",
                        }),

                        singleValue: (base) => ({
                          ...base,
                          color: "#111827",
                          fontSize: "14px",
                          fontWeight: "500",
                          margin: "0",
                        }),

                        indicatorsContainer: (base) => ({
                          ...base,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          paddingRight: "0px",
                        }),

                        dropdownIndicator: (base, state) => ({
                          ...base,
                          transition: "transform 0.2s ease",
                          transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
                          padding: "0",
                          marginLeft: "6px",
                        }),

                        option: (base, { isFocused }) => ({
                          ...base,
                          backgroundColor: isFocused ? "#f3f4f6" : "white",
                          color: "#111827",
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </div>
                </div>
              )}

            </div>
            {/* Next Focus Tips */}
            <div className="next-focus-container-inner">
              <NextFocus tips={displayData.next_focus_tips} isGuest={isGuest} />
            </div>

            {/* Unlock Full Feedback Button for Guest Users */}
            {isGuest && (
              <div className="flex justify-center w-full mt-8">
                <Button
                  onClick={handleUpgrade}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold px-8 py-3 rounded-lg shadow-lg transition-all duration-200 flex items-center gap-2"
                >
                  <Lock className="w-4 h-4" />
                  Unlock my full feedback
                </Button>
              </div>
            )}
          </div>

          {/* Actions */}
          {!isGuest && !isMobile && (
            <div className="task-analysis-footer flex justify-start" >
              <div className='task-analysis-footer-inner' onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
                <img src={rightarrow} alt="" />
                <button >
                  Back to Dashboard
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>


  );
};

export default ComprehensiveFeedbackResults;
